import { unifiedApi } from "@/common";
import {
  ISkyShields,
  ISkyWebrootDevice,
  ISubscriberDownloadsInfo,
  ISubscription,
  IWebrootGuestDownloadEmailRequest,
  IWebrootSubscriptions,
  WebrootDevicesInfoResponse,
} from "@/common/api/unifiedPortal/interfaces";
import { getUrl } from "@/common/getUrl";
import { logEvent, logException } from "@/common/logger";
import { criticalShields } from "@/common/securityDevices";
import {
  isNotActiveYet,
  redirectRenewWebrootToInterstitialPage,
  renewCarbWebrootSubscription,
  showDanger,
  showWarning,
  processSubscriptionDataForDisplay,
} from "@/common/subscriptionHelper";
import { addOnLCNs, webrootLicenseCategories } from "@/common/webrootProductName";
import { pageStates } from "@/components/Buy/BuyEnums";
import { getBuyRouteName } from "@/components/Buy/commonFn";
import { IShield, ISkyDevice, ITileData } from "@/components/Home";
import {
  EXCLUDED_FROM_SECURITY_AND_HOME_TAB_SECURITY,
  LONG_EXPIRED_DAYS,
  MAX_EMAIL_LENGTH,
  MIN_EMAIL_LENGTH,
  MSEC_IN_DAY,
  MSEC_IN_SECOND,
  SUBSCRIPTION_BESTBUY_RENEWAL_CONVERSION_DAY,
  SUBSCRIPTION_MIN_DATA_LOSS_DAY,
  URLS_JA,
  WEBROOT_PC_OPTIMIZER,
} from "@/define";
import { KeyCodeSeller, ProtectionStatus, StatusType, utmParams, ZuoraSubscriptionStatus } from "@/globalEnums";
import router from "@/routes";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { useNotificationsStore } from "@/stores/notifications";
import { useSecurityPageStore } from "@/stores/securityPage";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import { useUserStore } from "@/stores/user";
import { SortOrder } from "./Enums";
import { getBrowser, getOS } from "@/common/browserDetect";
import { formatKeycode } from "@/common/formatKeycode";
import { t } from "@/i18n";
import { reloadSubscriberInfo } from "@/common/reloadSubscriberInfo";
import { chromeOS } from "@/common/productList";
import { emailCharactersRegex } from "@/globalRegex";
import { isValidEmail } from "@/common/validateEmail";
import { IValidationResult, reportStringErrors } from "@/common/validator";

export function getSecurityData(subscriberInfo: ISubscriberDownloadsInfo): ITileData[] {
  const subData: ITileData[] = [];
  const securityPageStore = useSecurityPageStore();
  const skyDevices = securityPageStore.webrootDevices;
  const totalInstalledDevices = getInstalledDevices();

  // Remove items that aren't Active yet or Add-On
  let webrootSubs = subscriberInfo.webrootSubscriptions;
  const notActiveYetSubscriptions = webrootSubs.filter(item => isNotActiveYet(getSubscriptionInfo(item.keycode)));
  const activeSubscriptions = webrootSubs.filter(
    item =>
      !notActiveYetSubscriptions.includes(item) &&
      //remove Add-Ons
      !addOnLCNs.includes(item.licenseCategoryName)
  );

  // Order items with expiration date
  const orderedWebrootSubs = OrderSubscriptionsByExpiration(activeSubscriptions, "descending");
  // ZUOR-39109: Remove BBY S2 subs that are past the BBY S2 conversion date
  let filteredOrderedSubs = orderedWebrootSubs.filter(item => filterOutExpiredBestBuyKeycodes(item));
  // ZUOR-39155: Remove Safe keycodes for ActivationCodes that are purged
  filteredOrderedSubs = filteredOrderedSubs.filter(item => filterOutExpiredSafeKeycodes(item));
  // ZUOR-48885: Remove WIFI keycodes
  filteredOrderedSubs = filteredOrderedSubs.filter(item => filterOutWIFIKeycodes(item));
  // VE 4201423: Remove WSVPN keycode
  filteredOrderedSubs = filteredOrderedSubs.filter(item => filterOutWSVPNKeycodes(item));
  //  ZUOR-48952 ZUOR-49027 remove WW ADP WSASB
  filteredOrderedSubs = filteredOrderedSubs.filter(item => filterOutSunsetKeycodes(item));
  // Remove long expired keycodes -- Commented so we can display the same number of devices from the Home Tab
  //filteredOrderedSubs = filteredOrderedSubs.filter(item => filterOutLongExpiredKeycodes(item));

  // Add Not Active yet items to the beginning of the Array
  webrootSubs = [...filteredOrderedSubs, ...notActiveYetSubscriptions];

  webrootSubs.forEach(sub => {
    //Use keycodeAlias if it exists, otherwise use keycode
    const keycode = sub.keycodeAlias ?? sub.keycode;
    const subInfo = getSubscriptionInfo(keycode);
    if (subInfo?.licenseCategoryName !== webrootLicenseCategories.WPCO) {
      //The keycode from the Sky devices may be the alias, so we need to match the keycode or the alias to get the correct device info
      const matchedDeviceSub = skyDevices?.find(device => device.keycode === keycode);

      subData.push({
        title: sub.licenseCategoryName,
        type: "",
        status: getStatusType(subInfo, sub),
        state: "",
        seats: matchedDeviceSub?.seats,
        seatsConsumed: matchedDeviceSub?.seatsConsumed,
        licenseKey: matchedDeviceSub?.licenseKey,
        seatRestrictionEnforced: matchedDeviceSub?.seatRestrictionEnforced,
        devices: formatDevices(
          skyDevices !== null
            ? totalInstalledDevices.length > 0
              ? totalInstalledDevices.filter(result => result.keycode == keycode) || []
              : []
            : []
        ),
        keycode: keycode,
        identifier: keycode,
        WebrootLicenseCategoryName: sub.licenseCategoryName,
      });
    }
  });

  return subData;
}

export function getSubscriptionInfo(keycode: string | undefined): ISubscription | undefined {
  const securityPageStore = useSecurityPageStore();
  const subscriptionInformation = securityPageStore.subscriptionInformationForDisplay;
  // Added SKU to avoid getting wrong subscription in case of SafeLite
  return subscriptionInformation?.find(
    l =>
      (l.keyCode?.includes(keycode ?? "") || l.keycodeAlias === keycode) &&
      (l.sku?.toLowerCase() !== "personalbasic" || (l.sku?.toLowerCase() === "personalbasic" && l.isTrial))
  );
}

export function OrderSubscriptionsByExpiration(
  subs: IWebrootSubscriptions[],
  direction: "ascending" | "descending" | "none"
) {
  return subs.sort((a, b) => {
    const valueA = getExpirationDays(a.expirationDate);
    const valueB = getExpirationDays(b.expirationDate);

    if (valueA > valueB) {
      return direction === "ascending" ? 1 : -1;
    } else {
      return direction === "ascending" ? -1 : 1;
    }
  });
}

export function OrderTileDataByExpiration(data: ITileData[], direction: SortOrder) {
  return data.sort((a, b) => {
    const subA = getSubscriptionInfo(a.keycode);
    const subB = getSubscriptionInfo(b.keycode);
    const valueA = subA?.remainingDays !== undefined ? subA.remainingDays : Infinity;
    const valueB = subB?.remainingDays !== undefined ? subB.remainingDays : Infinity;
    if (valueA > valueB) {
      return direction === "ascending" ? 1 : -1;
    } else {
      return direction === "ascending" ? -1 : 1;
    }
  });
}

export function getExpirationDays(expirationDate: string) {
  const exp = new Date(expirationDate);
  const now = new Date();
  // Use UTC to avoid timezone issues
  const expUTC = Date.UTC(exp.getUTCFullYear(), exp.getUTCMonth(), exp.getUTCDate());
  const nowUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((expUTC - nowUTC) / MSEC_IN_DAY);
}

export function filterOutExpiredBestBuyKeycodes(sub: IWebrootSubscriptions) {
  const subInfo = getSubscriptionInfo(sub.keycode);

  if (subInfo?.keyCodeSellerType === KeyCodeSeller.BestBuy) {
    return subInfo?.remainingDays > SUBSCRIPTION_BESTBUY_RENEWAL_CONVERSION_DAY;
  }

  return true;
}

export function filterOutExpiredSafeKeycodes(sub: IWebrootSubscriptions) {
  const subInfo = getSubscriptionInfo(sub.keycode);

  if (subInfo?.keyCodeSellerType === KeyCodeSeller.Safe) {
    return subInfo?.remainingDays > SUBSCRIPTION_MIN_DATA_LOSS_DAY;
  }

  return true;
}

export function filterOutWIFIKeycodes(sub: IWebrootSubscriptions) {
  return sub.licenseCategoryName !== webrootLicenseCategories.WIFI;
}

export function filterOutWSVPNKeycodes(sub: IWebrootSubscriptions) {
  return sub.licenseCategoryName !== webrootLicenseCategories.WSVPN;
}

export function filterOutSunsetKeycodes(sub: IWebrootSubscriptions) {
  return !EXCLUDED_FROM_SECURITY_AND_HOME_TAB_SECURITY.includes(sub.licenseCategoryName);
}

export function filterOutLongExpiredKeycodes(sub: IWebrootSubscriptions) {
  const subInfo = getSubscriptionInfo(sub.keycode);

  if (subInfo) {
    return subInfo.remainingDays > LONG_EXPIRED_DAYS;
  }

  return true;
}

export function checkUnderMaintenance() {
  const userStore = useUserStore();
  const notificationsStore = useNotificationsStore();

  if (userStore.currentUser?.subscriberInfo?.isUnderMaintenance) {
    notificationsStore.addNotification({ type: "UnderMaintenance", dismissible: false });
    return;
  }
}

//NOT USED ANYMORE - kept for reference
// export function processNotifications(subData: ITileData[]) {
//   const notificationsStore = useNotificationsStore();
//   const userStore = useUserStore();
//   const securityPageStore = useSecurityPageStore();
//   if (userStore.currentUser?.subscriberInfo?.isUnderMaintenance) {
//     notificationsStore.addNotification({ type: "UnderMaintenance", dismissible: false });
//     return;
//   }

//   if (
//     subData.some(
//       l =>
//         (l.status === StatusType.danger && !webrootCDLPcodes.some(c => c === l.title)) ||
//         (l.status === StatusType.danger &&
//           !hasActiveVPNSubscription(securityPageStore.subscriptionInformationForDisplay) &&
//           l.title === webrootLicenseCategories.WSVPN)
//     )
//   ) {
//     notificationsStore.addNotification({ type: "SubscriptionsExpired", dismissible: false });
//     return;
//   }

//   if (subData.some(l => l.status === StatusType.warning)) {
//     notificationsStore.addNotification({ type: "SubscriptionsAlmostExpired", dismissible: false });
//     return;
//   }
// }

export function getStatusType(sub: ISubscription | undefined, webrootSub?: IWebrootSubscriptions): StatusType {
  let statusType = StatusType.normal;

  if (sub && showDanger(sub)) {
    statusType = StatusType.danger;
  } else if (sub && sub.zuoraSubscriptionStatus === ZuoraSubscriptionStatus.Cancelled) {
    statusType = StatusType.danger;
  } else if (sub && showWarning(sub)) {
    statusType = StatusType.warning;
  } else if (!sub && webrootSub) {
    if (isWebrootSubscriptionExpired(webrootSub)) {
      statusType = StatusType.danger;
    }
  }

  return statusType;
}

function isWebrootSubscriptionExpired(sub: IWebrootSubscriptions): boolean {
  const today = new Date().getTime();
  const subDate = new Date(sub.expirationDate).getTime();

  return subDate < today;
}

export function getInstalledDevices(): ISkyWebrootDevice[] {
  const securityPageStore = useSecurityPageStore();
  const devices: ISkyWebrootDevice[] = [];
  securityPageStore.webrootDevices.forEach(result => {
    if (result.devices) {
      result.devices?.forEach(d => {
        if (d.isActive ?? true) {
          // Null means it's active too.
          devices.push(d);
        }
      });
    }
  });
  return devices;
}

export function formatDevices(devices: ISkyWebrootDevice[]) {
  const formattedDevices: ISkyDevice[] = [];

  devices.forEach(device => {
    formattedDevices.push({
      name: device.hostName,
      nickname: device.deviceNickname,
      keycode: device.keycode,
      instanceId: device.instanceId,
      id: device.deviceId,
      isVisible: device.isVisible !== null ? device.isVisible : true,
      operatingSystem: device.operatingSystem,
      osBuild: device.osBuild || "",
      osPlatform: device.osPlatform,
      type: device.deviceType,
      version: device.version || "",
      shields: getShieldsInfo(device.shields),
      isActive: device.isActive,
      lastSeen: device.lastSeen ? new Date(device.lastSeen * MSEC_IN_SECOND).toDateString() : undefined,
    });
  });

  return formattedDevices;
}

export function getShieldsInfo(deviceShields: ISkyShields) {
  const shields: IShield[] = [];

  Object.entries(deviceShields).map(entry => {
    const key = entry[0];
    const value = entry[1];
    if (value !== null && value !== undefined) {
      shields.push({ name: key, value: value });
    }
  });

  return orderShieldsAlphabetically(shields);
}

export function orderShieldsAlphabetically(shields: IShield[]) {
  return shields.sort(function (a, b) {
    const nameA = a.name.toUpperCase(); // ignore upper and lowercase
    const nameB = b.name.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1; //nameA comes first
    }
    if (nameA > nameB) {
      return 1; // nameB comes first
    }
    return 0; // names must be equal
  });
}

export function isExpired(data: ITileData): boolean {
  const subscriptionInformation = getSubscriptionInfo(data.keycode);

  //Adding check here since Sky devices can sometimes come back as inactive and cancelled.
  if (subscriptionInformation?.zuoraSubscriptionStatus === ZuoraSubscriptionStatus.Cancelled) {
    return true;
  }

  return (
    data.status === StatusType.danger &&
    !isNotActiveYet(subscriptionInformation) &&
    !subscriptionInformation?.hasOutstandingInvoices
  );
}

//TODO: This currently uses the old icons from Security V1, will need to be updated
export function getDeviceIcon(deviceData: ISkyDevice) {
  let icon = "";
  const deviceType = deviceData.type ? deviceData.type.toLowerCase() : "";
  const operatingSystem = deviceData.operatingSystem ? deviceData.operatingSystem.toLowerCase() : "";

  if (
    (deviceType && (deviceType.includes("pc") || deviceType.includes("windows"))) ||
    operatingSystem.includes("windows")
  ) {
    icon = "security-windows";
  }

  if (
    (deviceType && deviceType.includes("mac")) ||
    deviceType.includes("os x") ||
    operatingSystem.includes("mac") ||
    operatingSystem.includes("os x")
  ) {
    icon = "security-mac-os";
  }
  //Next few are not really implemented from SKY atm
  if ((deviceType && deviceType.includes("ios")) || operatingSystem.includes("ios")) {
    icon = "security-ios";
  }

  if ((deviceType && deviceType.includes("android")) || operatingSystem.includes("android")) {
    icon = "security-android";
  }

  if ((deviceType && deviceType.includes("chrome")) || operatingSystem.includes("chrome")) {
    icon = "security-chrome-os";
  }

  return icon;
}

export function deviceClass(device: ISkyDevice, data: ITileData): Record<string, string> {
  const deviceClass = {};

  if (device.shields.some(l => criticalShields.includes(l.name) && !l.value) || isExpired(data)) {
    deviceClass["danger"] = true;
  } else if (device.shields.some(l => !l.value)) {
    deviceClass["warning"] = true;
  } else {
    deviceClass["success"] = true;
  }

  return deviceClass;
}

export async function renewWebrootSubscription(
  subscriptionInformation: ISubscription | undefined,
  webrootSubscriptionInformation: IWebrootSubscriptions | undefined
) {
  const notificationsStore = useNotificationsStore();
  notificationsStore.clearNotifications();

  if (subscriptionInformation?.hasOutstandingInvoices) {
    router.push({
      name: "subscription_information",
    });
    return;
  }

  //Check for PC Optimizer. This is temporary until the interstitial page is updated
  if (subscriptionInformation?.sku === WEBROOT_PC_OPTIMIZER) {
    const routeName = getBuyRouteName();
    router.push({
      name: routeName,
      query: {
        step: pageStates.email,
        sn: subscriptionInformation?.zuoraSubscriptionNumber,
        lk: subscriptionInformation?.licenseKey || "",
        sku: WEBROOT_PC_OPTIMIZER,
        sm: subscriptionInformation?.subscriptionMonths,
      },
    });

    return;
  }

  //use alias if it exists, otherwise use keycode
  const keycode = webrootSubscriptionInformation?.keycodeAlias || webrootSubscriptionInformation?.keycode || "";

  await redirectRenewWebrootToInterstitialPage(keycode);
}

export async function buyWebrootSubscription(
  subscriptionInformation: ISubscription | undefined,
  webrootSubscriptionInformation: IWebrootSubscriptions | undefined
) {
  const userStore = useUserStore();
  const notificationsStore = useNotificationsStore();
  const buyFlowStore = useBuyFlowStore();
  const componentName = "SecurityPageHelper";

  notificationsStore.clearNotifications();
  if (subscriptionInformation?.isTrial) {
    const ratePlanId = buyFlowStore.getRatePlanIdForTrialSubscription(subscriptionInformation);
    const routeName = getBuyRouteName();
    if (ratePlanId) {
      router.push({
        name: routeName,
        query: { rpi: ratePlanId, lk: subscriptionInformation.licenseKey, step: pageStates.email },
      });
      return;
    }
  }

  try {
    //use alias if it exists, otherwise use keycode
    const keycode = webrootSubscriptionInformation?.keycodeAlias || webrootSubscriptionInformation?.keycode || "";
    if (keycode) {
      let url;
      if (userStore.selectedLanguage === "ja") {
        url = URLS_JA["WEBROOT_HOME"];
      } else {
        url = (await unifiedApi.getWebrootRenewUrl(keycode)).data.redirectUrl;
      }

      window.open(url, "_self");
    }
  } catch (err) {
    notificationsStore.addNotification({ type: "ServerErrors" });
    logException(
      err as Error,
      `${componentName}_buyWebrootSubscription, User: ${userStore?.currentUser?.personId ?? ""}`
    );
  }
}

export function contactBbyLink() {
  const subscriptionsStore = useSubscriptionsStore();
  //Refresh because the user may have changed some thing off site.
  subscriptionsStore.forceRefresh();
  window.open(getUrl("BESTBUY_URL"), "_blank");
}

export function renewSafeWebroot(subscriptionInformation: ISubscription | undefined) {
  renewCarbWebrootSubscription(subscriptionInformation, utmParams.renew, utmParams.security_tab);
}

export function renewSafe(subscriptionInformation: ISubscription | undefined) {
  const routeName = getBuyRouteName();
  router.push({
    name: routeName,
    query: {
      step: pageStates.payment,
      deviceId: subscriptionInformation?.computer?.id,
      sku: subscriptionInformation?.sku,
    },
  });
}

export function buySafe(subscriptionInformation: ISubscription | undefined) {
  router.push({
    name: "selectPlan",
    query: {
      deviceId: subscriptionInformation?.computer?.id,
      sku: subscriptionInformation?.sku,
    },
  });
}

export function getWebrootSubInfo(keycode: string | undefined): IWebrootSubscriptions | undefined {
  const securityPageStore = useSecurityPageStore();
  //Check both keycode and keycode alias to find the subscription information for this subscriptionData
  return securityPageStore.subscriberInfo?.webrootSubscriptions.find(
    l => l.keycode === keycode || l.keycodeAlias === keycode
  );
}

export function copyKeycodeToClipboard(keycodeText: string) {
  try {
    navigator.clipboard.writeText(formatKeycode(keycodeText));
  } catch (err) {
    logException(err as Error, {
      Method: "Copy",
      keycodeText: keycodeText,
      FormattedKeyCode: formatKeycode(keycodeText),
      Browser: getBrowser(),
      Os: getOS(),
    });
  }
}

export function filterSecurityDevices(devices: ISkyDevice[]) {
  return devices.filter(device => device.osBuild !== chromeOS && device.operatingSystem !== chromeOS);
}

export function sortDevicesByName(devices: ISkyDevice[], order: SortOrder) {
  return [...devices].sort((a, b) => {
    const nameA = a.nickname || a.name.toUpperCase();
    const nameB = b.nickname || b.name.toUpperCase();
    if (nameA < nameB) {
      return order === SortOrder.ASCENDING ? -1 : 1;
    }
    if (nameA > nameB) {
      return order === SortOrder.ASCENDING ? 1 : -1;
    }
    return 0;
  });
}

export async function refreshSecurity() {
  const securityPageStore = useSecurityPageStore();
  const subscriptionsStore = useSubscriptionsStore();
  logEvent("refreshSecurity", "SecurityPageHelper");
  const [, , , subscriberInfoResponse] = await Promise.all([
    subscriptionsStore.forceRefreshAndPopulateSubscriptions(),
    reloadSubscriberInfo(),
    securityPageStore.populateSecurityPageData(true),
    unifiedApi.getSubscriberDownloadsInfo(),
  ]);
  const subs = subscriptionsStore.subscriptions;
  securityPageStore.subscriptionInformationForDisplay = processSubscriptionDataForDisplay(subs);
  securityPageStore.subscriberInfo = subscriberInfoResponse.data;
  if (securityPageStore.subscriberInfo) {
    securityPageStore.subData = getSecurityData(securityPageStore.subscriberInfo);
  } else {
    logEvent(`getSubscriberDownloadsInfo result NULL`, `Security Page V2 /getSecurityData`);
  }
  //await getLastPassInfo();
  //isBestBuyS2.value = await hasOnlyBestBuyS2();
  logEvent("refreshSecurity Completed", "SecurityPageHelper");
}

export function getDeviceStatusIcon(status: ProtectionStatus): string {
  switch (status) {
    case ProtectionStatus.protected:
      return "device-status-protected";
    case ProtectionStatus.protectionDisabled:
      return "device-status-danger";
    case ProtectionStatus.shieldsDisabled:
      return "device-status-warning";
    case ProtectionStatus.infoMissing:
      return "device-status-info";
    default:
      return "device-status-info";
  }
}

export function getRowClass(status: ProtectionStatus): string {
  switch (status) {
    case ProtectionStatus.protected:
      return "row-success";
    case ProtectionStatus.protectionDisabled:
      return "row-danger";

    case ProtectionStatus.shieldsDisabled:
      return "row-warning";
    case ProtectionStatus.infoMissing:
      return "";
    default:
      return "";
  }
}
export function getDeviceProtectionStatus(shields: IShield[]): ProtectionStatus {
  let status = ProtectionStatus.protected;
  if (!shields || shields.length === 0) {
    status = ProtectionStatus.infoMissing;
  } else if (shields.some(l => criticalShields.includes(l.name) && !l.value)) {
    status = ProtectionStatus.protectionDisabled;
  } else if (shields.some(l => !l.value)) {
    status = ProtectionStatus.shieldsDisabled;
  }

  return status;
}

export function shieldClass(protectionStatus: ProtectionStatus): string {
  switch (protectionStatus) {
    case ProtectionStatus.protected:
      return "success-text";
    case ProtectionStatus.infoMissing:
      return "info-text";
    case ProtectionStatus.shieldsDisabled:
    case ProtectionStatus.protectionDisabled:
      return "danger-text";
    default:
      return "info-text";
  }
}

export function getDeviceShieldsStatus(shields: IShield[], protectionStatus: ProtectionStatus): string[] {
  let shieldStatuses: string[] = [];
  if (protectionStatus === ProtectionStatus.protected) {
    shieldStatuses = [t("DeviceInfoComponent.allShields")];
  } else if (protectionStatus === ProtectionStatus.infoMissing) {
    shieldStatuses = [t("DeviceInfoComponent.infoMissing")];
  } else if (protectionStatus === ProtectionStatus.protectionDisabled) {
    shieldStatuses = shields
      .filter(l => criticalShields.includes(l.name) && !l.value)
      .map(l => t(`DeviceInfoComponent.${l.name}`));
  } else if (protectionStatus === ProtectionStatus.shieldsDisabled) {
    shieldStatuses = shields.filter(l => !l.value).map(l => t(`DeviceInfoComponent.${l.name}`));
  }

  return shieldStatuses;
}

export async function updateDeviceNickName(deviceInfo: ISkyDevice, newNickname: string, componentName: string) {
  try {
    const securityPageStore = useSecurityPageStore();
    //Look up the subscriptionInformation for the device to get the keycode or keycodeAlias
    const sub = securityPageStore.subscriptionInformationForDisplay.find(
      l => l.keyCode === deviceInfo?.keycode || l.keycodeAlias === deviceInfo?.keycode
    );
    //use the alias if there is one, otherwise use the keycode. If there is no subscriptionInformation, use the keycode from the device
    const keycode = sub?.keycodeAlias || sub?.keyCode || "";

    const request = {
      deviceId: deviceInfo.id,
      instanceId: deviceInfo.instanceId,
      deviceNickname: newNickname,
      isVisible: deviceInfo.isVisible,
      keycode: keycode,
    };

    await unifiedApi.updateWebrootDevice(request);
  } catch (err) {
    const userStore = useUserStore();
    const notificationsStore = useNotificationsStore();
    notificationsStore.addNotification({ type: "ServerErrors" });
    logException(err as Error, `${componentName}, User: ${userStore?.currentUser?.personId ?? ""}`);
  }
}

export function goToHowToEnableBtnLink(isCdlp: boolean) {
  if (isCdlp) {
    window.open(getUrl("WEBROOT_HOW_TO_ENABLE_CDLP"), "_blank");
  } else {
    window.open(getUrl("WEBROOT_HOW_TO_ENABLE"), "_blank");
  }
}

export function deviceTypeIcon(deviceInfo: ISkyDevice): string {
  let icon = "desktop";
  const deviceType = deviceInfo.type
    ? deviceInfo.type.toLowerCase()
    : deviceInfo.operatingSystem
      ? deviceInfo.operatingSystem.toLowerCase()
      : "";
  if (
    (deviceType && (deviceType.includes("pc") || deviceType.includes("windows") || deviceType.includes("mac"))) ||
    deviceType.includes("os x") ||
    deviceType.includes("chrome")
  ) {
    icon = "laptop";
  }

  //Next few are not really implemented from SKY atm
  if (deviceType && (deviceType.includes("ios") || deviceType.includes("android"))) {
    icon = "mobile-devices";
  }

  return icon;
}

//There is a bug in SKY for mobile devices where they are all marked as inactive.
//Mobile devices also do not have shields, so we will add shields to them for display purposes.
export function fixMobileDevices(webrootDevices: WebrootDevicesInfoResponse[]) {
  if (!webrootDevices) {
    return [];
  }

  webrootDevices.forEach(device => {
    device.devices?.forEach(d => {
      if (isDeviceMobile(d.deviceType || d.operatingSystem || "")) {
        d.shields = Object.assign(d.shields, {
          realtimeShield: true,
          webShield: true,
          phishingShield: true,
          rootkitShield: true,
          usbShield: true,
          offlineShield: true,
          scriptShield: true,
          identityShield: true,
        });
      }
    });
  });
  return webrootDevices;
}

export function isDeviceMobile(deviceType: string): boolean {
  const type = deviceType ? deviceType.toLowerCase() : "";
  return type.includes("ios") || type.includes("android");
}

export function operatingSystemIcon(deviceInfo: ISkyDevice): string {
  let icon = "";
  const operatingSystem = deviceInfo.operatingSystem ? deviceInfo.operatingSystem.toLowerCase() : "";

  if (operatingSystem.includes("windows")) {
    icon = "windows";
  }

  if (operatingSystem.includes("mac") || operatingSystem.includes("os x")) {
    icon = "apple";
  }
  //Next few are not really implemented from SKY atm
  if (operatingSystem.includes("ios")) {
    icon = "apple";
  }

  if (operatingSystem.includes("android")) {
    icon = "android";
  }

  if (operatingSystem.includes("chrome")) {
    icon = "chrome";
  }

  return icon;
}

export function operatingSystemTitle(deviceInfo: ISkyDevice): string {
  const operatingSystem = deviceInfo.operatingSystem ? deviceInfo.operatingSystem.toLowerCase() : "";

  if (operatingSystem.includes("windows")) {
    return t("Common.windows");
  }

  if (operatingSystem.includes("mac") || operatingSystem.includes("os x")) {
    return t("Common.macos");
  }
  //Next few are not really implemented from SKY atm
  if (operatingSystem.includes("ios")) {
    return t("Common.ios");
  }

  if (operatingSystem.includes("android")) {
    return t("Common.android");
  }

  if (operatingSystem.includes("chrome")) {
    return t("Common.chromeOs");
  }
  return "";
}

/**
 * Filters and returns only valid email addresses from the provided array.
 * Validates email format including @ and . symbols, length constraints, and character restrictions.
 * @param emails - Array of email strings
 * @returns Array of valid email addresses (trimmed)
 */
export function getValidEmails(emails: string[]): string[] {
  return emails
    .filter(email => {
      const trimmedEmail = email.trim();
      // Check for empty string
      if (trimmedEmail === "") {
        return false;
      }
      // Check for @ and . symbols
      if (!trimmedEmail.includes("@") || !trimmedEmail.includes(".")) {
        return false;
      }
      // Check min and max length
      if (trimmedEmail.length < MIN_EMAIL_LENGTH || trimmedEmail.length > MAX_EMAIL_LENGTH) {
        return false;
      }
      // Check for invalid characters using regex
      // Reset lastIndex to avoid issues with global flag
      emailCharactersRegex.lastIndex = 0;
      return !emailCharactersRegex.test(trimmedEmail);
    })
    .map(email => email.trim());
}

export function getEmailInvalidCharacterError(email: string): string {
  return reportStringErrors(email, t("Common.email"), MIN_EMAIL_LENGTH, MAX_EMAIL_LENGTH, emailCharactersRegex);
}

export function getEmailError(
  email: string,
  isDirty: boolean,
  allEmails?: string[],
  currentIndex?: number
): IValidationResult | null {
  if (!isDirty) return null;
  const errs = isValidEmail(email).filter(e => !e.passed);
  if (errs.length > 0) return errs[0];
  const charError = getEmailInvalidCharacterError(email);
  if (charError.length > 0) {
    return { name: "invalidCharacter", message: charError } as IValidationResult;
  }
  if (allEmails && currentIndex !== undefined && allEmails.find((r, i) => r === email && i !== currentIndex)) {
    return { name: "duplicateEmail", message: t("SecurityComponentV2.duplicateEmailError") } as IValidationResult;
  }
  return null;
}

export async function sendEmail(emailLinkRows: string[], keycode: string, productCode: string) {
  try {
    // POST api/cp/email/guest/download/send
    const validEmails = getValidEmails(emailLinkRows);
    if (validEmails.length === 0) {
      return false;
    }
    const request: IWebrootGuestDownloadEmailRequest = {
      emails: validEmails,
      keyCode: keycode,
      productCode: productCode,
    };
    await unifiedApi.sendWebrootGuestDownloadEmail(request);
    return true;
  } catch {
    return false;
  }
}

// currently all the conditions are mutually exclusive and we can only have one banner showing at a time,
// but we will keep it as an array in case we want to show multiple banners at once in the future
export function getBanners(
  installedDeviceCount: number,
  deviceLimit: number,
  isExpiring: boolean,
  subscriptionData: ITileData
): string[] {
  const bannersList: string[] = [];
  if (installedDeviceCount == deviceLimit && !isExpiring && !isExpired(subscriptionData)) {
    if (subscriptionData.title == webrootLicenseCategories.WSAV && !!subscriptionData.seatRestrictionEnforced) {
      bannersList.push(t("SecurityComponentV2.seatLimitReachedUpgrade"));
    } else {
      bannersList.push(t("SecurityComponentV2.seatLimitReached"));
    }
  }
  if (installedDeviceCount > deviceLimit && !isExpiring && !isExpired(subscriptionData)) {
    const excess = installedDeviceCount - deviceLimit;
    const excessDevices =
      excess === 1 ? `${excess} ${t("Common.device").toLowerCase()}` : `${excess} ${t("Common.devices").toLowerCase()}`;
    if (subscriptionData.title == webrootLicenseCategories.WSAV && !!subscriptionData.seatRestrictionEnforced) {
      bannersList.push(t("SecurityComponentV2.seatLimitExceededUpgrade", { excessDevices }));
    } else {
      bannersList.push(t("SecurityComponentV2.seatLimitExceeded", { excessDevices }));
    }
  }

  return bannersList;
}

export function expiredSectionStartsCollapsed() {
  const securityPageStore = useSecurityPageStore();

  if (securityPageStore.activeLicenses.length > 0) {
    return true;
  }

  if (securityPageStore.expiredSubscriptions.length > 0) {
    return false;
  }

  return true;
}
